<template>
  <v-dialog :value="value" :persistent="loading || saving" max-width="680" scrollable @input="$emit('input', $event)">
    <v-card :loading="loading || saving">
      <v-card-title>{{ $t('ReserveCommonNotes.settingsTitle') }}</v-card-title>
      <v-card-subtitle>{{ $t('ReserveCommonNotes.settingsDescription') }}</v-card-subtitle>

      <v-card-text style="max-height: 65vh;">
        <v-alert v-if="loadFailed" type="error" text dense>
          {{ $t('ReserveCommonNotes.loadFailed') }}
          <v-btn text small color="error" @click="loadNotes">
            {{ $t('ReserveCommonNotes.retry') }}
          </v-btn>
        </v-alert>
        <v-alert v-if="needsReload" type="warning" text dense>
          {{ $t('ReserveCommonNotes.staleData') }}
          <v-btn text small color="warning" @click="loadNotes">
            {{ $t('ReserveCommonNotes.retry') }}
          </v-btn>
        </v-alert>

        <v-row
          v-for="(item, index) in draftItems"
          :key="index"
          dense
          class="align-start mb-1"
        >
          <v-col cols="12" sm="2" class="pt-4">
            <v-chip small>{{ $t('ReserveCommonNotes.itemLabel', {index: index + 1}) }}</v-chip>
          </v-col>
          <v-col cols="12" sm="10">
            <v-textarea
              :value="item.content"
              :placeholder="$t('ReserveCommonNotes.inputPlaceholder')"
              :error-messages="rowErrorMessages(index)"
              outlined
              dense
              rows="2"
              auto-grow
              no-resize
              :disabled="loading || saving"
              @input="updateContent(index, $event)"
            />
            <div class="text-caption grey--text text--darken-1 text-right mt-n3 mb-1">
              {{ $t('ReserveCommonNotes.counter', {
                characters: characterCount(item.content),
                lines: lineCount(item.content)
              }) }}
            </div>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions>
        <v-spacer/>
        <v-btn text :disabled="loading || saving" @click="close">
          {{ $t('GENERAL.cancel') }}
        </v-btn>
        <v-btn color="primary" :loading="saving" :disabled="loading || loadFailed || needsReload" @click="save">
          {{ $t('ReserveCommonNotes.save') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {mapGetters} from 'vuex'
import {getReserveNoteTemplatesAPI, updateReserveNoteTemplatesAPI} from '@/api'
import {
  countCommonNoteCharacters,
  countCommonNoteLines,
  createCommonNoteDraft,
  validateCommonNoteDraft,
} from '@/utils/reserveCommonNotes'

export default {
  name: 'ReserveCommonNoteSettingsDialog',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      draftItems: createCommonNoteDraft(),
      rowErrors: createCommonNoteDraft().map(() => []),
      loading: false,
      saving: false,
      loadFailed: false,
      needsReload: false,
      loadRequestId: 0,
    }
  },
  computed: {
    ...mapGetters(['axiosParamData']),
  },
  watch: {
    value(isOpen) {
      if (isOpen) this.loadNotes()
      else this.loadRequestId += 1
    },
  },
  methods: {
    extractItems(response) {
      const payload = response && response.data
      if (payload && Array.isArray(payload.data)) return payload.data
      return Array.isArray(payload) ? payload : []
    },
    loadNotes() {
      const requestId = ++this.loadRequestId
      this.loading = true
      this.loadFailed = false
      this.needsReload = false
      getReserveNoteTemplatesAPI(this.axiosParamData)
        .then(response => {
          if (requestId !== this.loadRequestId) return
          this.draftItems = createCommonNoteDraft(this.extractItems(response))
          this.rowErrors = this.draftItems.map(() => [])
        })
        .catch(() => {
          if (requestId !== this.loadRequestId) return
          this.loadFailed = true
          this.$notify.error(this.$t('ReserveCommonNotes.loadFailed'))
        })
        .finally(() => {
          if (requestId !== this.loadRequestId) return
          this.loading = false
        })
    },
    updateContent(index, content) {
      this.$set(this.draftItems[index], 'content', content)
      this.rowErrors = validateCommonNoteDraft(this.draftItems).errors
    },
    characterCount(content) {
      return countCommonNoteCharacters(content.trim())
    },
    lineCount(content) {
      return countCommonNoteLines(content.trim())
    },
    validationMessage(error) {
      if (error.code === 'MAX_CHARACTERS') return this.$t('ReserveCommonNotes.maxCharacters')
      if (error.code === 'MAX_LINES') return this.$t('ReserveCommonNotes.maxLines')
      if (error.code === 'DUPLICATE_CONTENT') {
        return this.$t('ReserveCommonNotes.duplicateContent', {rows: error.rows.join('、')})
      }
      return this.$t('ReserveCommonNotes.invalidContent')
    },
    rowErrorMessages(index) {
      return this.rowErrors[index].map(this.validationMessage)
    },
    applyServerErrors(error) {
      const payload = error && error.response && error.response.data
      const serverErrors = payload && Array.isArray(payload.errors) ? payload.errors : []

      serverErrors.forEach(serverError => {
        const rows = serverError.rows || []
        rows.forEach(row => {
          const index = Number(row) - 1
          if (index < 0 || index >= this.rowErrors.length) return
          this.rowErrors[index].push({
            code: serverError.code,
            rows,
          })
        })
      })

      if (serverErrors.some(serverError => serverError.code === 'INVALID_ITEM_ID')) {
        this.needsReload = true
        return this.$t('ReserveCommonNotes.staleData')
      }

      return (payload && payload.messages) || this.$t('ReserveCommonNotes.saveFailed')
    },
    save() {
      const validation = validateCommonNoteDraft(this.draftItems)
      this.rowErrors = validation.errors
      if (!validation.valid) return

      this.saving = true
      updateReserveNoteTemplatesAPI({
        ...this.axiosParamData,
        items: validation.items,
      })
        .then(response => {
          const items = this.extractItems(response)
          this.draftItems = createCommonNoteDraft(items)
          this.$emit('saved', items)
          this.$notify.success(this.$t('ReserveCommonNotes.saved'))
          this.close()
        })
        .catch(error => {
          this.$notify.error(this.applyServerErrors(error))
        })
        .finally(() => {
          this.saving = false
        })
    },
    close() {
      this.$emit('input', false)
    },
  },
}
</script>
